import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
  Modal,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { medicalEquipmentService } from '../../../../services/medicalEquipment';
import { US_STATES } from '../../../../constants/states';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('MedicalEquipmentInput');

type MedicalEquipmentInputScreenRouteProp = RouteProp<RootStackParamList, 'MedicalEquipmentInput'>;

// Helper: format phone as (xxx) xxx-xxxx while typing
const formatPhoneNumber = (text: string) => {
  const cleaned = (text || '').replace(/\D/g, '');
  if (cleaned.length >= 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  } else if (cleaned.length >= 6) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else if (cleaned.length >= 3) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  }
  return cleaned;
};

export const MedicalEquipmentInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<MedicalEquipmentInputScreenRouteProp>();

  const { medicalEquipmentData, isEditing } = route.params || {};

  const [equipmentType, setEquipmentType] = useState(medicalEquipmentData?.equipmentType || '');
  const [companyName, setCompanyName] = useState(medicalEquipmentData?.companyName || '');
  const [location, setLocation] = useState(medicalEquipmentData?.location || '');
  const [phone, setPhone] = useState(formatPhoneNumber(medicalEquipmentData?.phone || ''));
  const [email, setEmail] = useState(medicalEquipmentData?.email || '');
  const [state, setState] = useState(medicalEquipmentData?.state || '');
  const [notes, setNotes] = useState(medicalEquipmentData?.notes || '');
  const [rentalCost, setRentalCost] = useState(medicalEquipmentData?.rentalCost || '');
  const [startDate, setStartDate] = useState(medicalEquipmentData?.startDate || '');
  const [contractEnd, setContractEnd] = useState(medicalEquipmentData?.contractEnd || '');
  const [contactPerson, setContactPerson] = useState(medicalEquipmentData?.contactPerson || '');
  const [maintenanceSchedule, setMaintenanceSchedule] = useState(medicalEquipmentData?.maintenanceSchedule || '');
  const [serialNumber, setSerialNumber] = useState(medicalEquipmentData?.serialNumber || '');
  const [modelNumber, setModelNumber] = useState(medicalEquipmentData?.modelNumber || '');
  const [saving, setSaving] = useState(false);
  const [showAddAnotherDone, setShowAddAnotherDone] = useState(false);
  const [savedMedicalEquipmentId, setSavedMedicalEquipmentId] = useState<string | undefined>();
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);
  const [showStateModal, setShowStateModal] = useState(false);

  // Set toggle to true if editing and any additional fields have data
  useEffect(() => {
    if (isEditing && medicalEquipmentData) {
      const hasAdditionalData = medicalEquipmentData.rentalCost ||
                               medicalEquipmentData.startDate ||
                               medicalEquipmentData.contractEnd ||
                               medicalEquipmentData.contactPerson ||
                               medicalEquipmentData.maintenanceSchedule ||
                               medicalEquipmentData.serialNumber ||
                               medicalEquipmentData.modelNumber;
      setShowAdditionalInfo(!!hasAdditionalData);
    }
  }, [isEditing, medicalEquipmentData]);

  const handleSave = async () => {
    // Validation - require at least equipment type
    if (!equipmentType.trim()) {
      Alert.alert('Error', 'Please enter at least an equipment type');
      return;
    }

    try {
      setSaving(true);

      const medicalEquipmentUpdateData = {
        taskId: 11, // Medical Equipment task ID
        equipmentType: equipmentType.trim(),
        companyName: companyName.trim(),
        location: location.trim(),
        phone: phone.replace(/\D/g, ''),
        email: email.trim(),
        state: state.trim(),
        notes: notes.trim(),
        rentalCost: rentalCost.trim(),
        startDate: startDate.trim(),
        contractEnd: contractEnd.trim(),
        contactPerson: contactPerson.trim(),
        maintenanceSchedule: maintenanceSchedule.trim(),
        serialNumber: serialNumber.trim(),
        modelNumber: modelNumber.trim(),
      };

      let savedMedicalEquipment;

      if (isEditing && medicalEquipmentData?.id) {
        // Update existing record
        savedMedicalEquipment = await medicalEquipmentService.updateMedicalEquipment(
          medicalEquipmentData.id,
          medicalEquipmentUpdateData
        );
        // Handle wrapped response format { success, data, message }
        const updatedData = savedMedicalEquipment?.data || savedMedicalEquipment;
        logger.debug('Medical equipment updated successfully with ID:', updatedData.id);

        // Navigate to lock/unlock screen to allow re-selection
        navigation.navigate('MedicalEquipmentLockUnlock', { medicalEquipmentId: updatedData.id });
      } else {
        // Create new record
        savedMedicalEquipment = await medicalEquipmentService.createMedicalEquipment(medicalEquipmentUpdateData);
        // Handle wrapped response format { success, data, message }
        const createdData = savedMedicalEquipment?.data || savedMedicalEquipment;
        logger.debug('Medical equipment saved successfully with ID:', createdData.id);

        // Store the saved ID for the Done button
        setSavedMedicalEquipmentId(createdData.id);

        // Clear the form and reset states for Add Another
        setEquipmentType('');
        setCompanyName('');
        setLocation('');
        setPhone('');
        setEmail('');
        setState('');
        setNotes('');
        setRentalCost('');
        setStartDate('');
        setContractEnd('');
        setContactPerson('');
        setMaintenanceSchedule('');
        setSerialNumber('');
        setModelNumber('');
        setShowAdditionalInfo(false);

        // Show Add Another / Done buttons after successful save
        setShowAddAnotherDone(true);
        // Reset saving so buttons are interactive
        setSaving(false);
      }
    } catch (error) {
      logger.error('Error saving medical equipment:', error);
      Alert.alert('Error', 'Failed to save medical equipment information. Please try again.');
      setSaving(false);
    }
  };

  const handleAddAnother = () => {
    // Clear form and hide buttons
    setEquipmentType('');
    setCompanyName('');
    setLocation('');
    setPhone('');
    setEmail('');
    setState('');
    setNotes('');
    setRentalCost('');
    setStartDate('');
    setContractEnd('');
    setContactPerson('');
    setMaintenanceSchedule('');
    setSerialNumber('');
    setModelNumber('');
    setShowAddAnotherDone(false);
    setShowAdditionalInfo(false);
    setSaving(false);
  };

  const handleDone = async () => {
    setSaving(false);
    logger.debug('[Done Debug] Using ID:', savedMedicalEquipmentId);

    // If we don't have a saved ID, get the most recent medical equipment entry
    if (!savedMedicalEquipmentId) {
      try {
        const response = await medicalEquipmentService.getMedicalEquipment(11);
        logger.debug('[Done Debug] API response:', response);

        // Handle response format - check if it's wrapped in a success/data object
        const allMedicalEquipment = response?.data || response;
        logger.debug('[Done Debug] Extracted medical equipment array:', allMedicalEquipment);

        const mostRecent = allMedicalEquipment && Array.isArray(allMedicalEquipment) && allMedicalEquipment.length > 0 ? allMedicalEquipment[allMedicalEquipment.length - 1] : null;
        if (mostRecent) {
          logger.debug('[Done Debug] Using most recent ID:', mostRecent.id);
          navigation.navigate('MedicalEquipmentLockUnlock', { medicalEquipmentId: mostRecent.id });
        } else {
          logger.debug('[Done Debug] No medical equipment entries found in array:', allMedicalEquipment);
          Alert.alert('Error', 'No medical equipment information found. Please create an entry first.');
        }
      } catch (error) {
        logger.error('[Done Debug] Error getting medical equipment:', error);
        Alert.alert('Error', 'Failed to get medical equipment information.');
      }
    } else {
      navigation.navigate('MedicalEquipmentLockUnlock', { medicalEquipmentId: savedMedicalEquipmentId });
    }
  };

  const handleStateSelect = (state: string) => {
    setState(state);
    setShowStateModal(false);
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>
          Add Your Medical Equipment Information
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Equipment Type *</Text>
            <TextInput
              style={styles.input}
              value={equipmentType}
              onChangeText={setEquipmentType}
              placeholder="Ex: CPAP Machine, Wheelchair, etc."
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Company Name</Text>
            <TextInput
              style={styles.input}
              value={companyName}
              onChangeText={setCompanyName}
              placeholder="Equipment rental/supply company"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Location</Text>
            <TextInput
              style={styles.input}
              value={location}
              onChangeText={setLocation}
              placeholder="Where is the equipment located?"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Phone</Text>
            <TextInput
              style={styles.input}
              value={phone}
              onChangeText={(text) => setPhone(formatPhoneNumber(text))}
              placeholder="(xxx) xxx-xxxx"
              placeholderTextColor="#A9A9A9"
              keyboardType="phone-pad"
              maxLength={14}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Email</Text>
            <TextInput
              style={styles.input}
              value={email}
              onChangeText={setEmail}
              placeholder="Company email address"
              placeholderTextColor="#A9A9A9"
              keyboardType="email-address"
              autoCapitalize="none"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>State</Text>
            <TouchableOpacity
              style={styles.input}
              onPress={() => setShowStateModal(true)}
            >
              <Text style={state ? styles.inputText : styles.placeholderText}>
                {state || "Select State"}
              </Text>
            </TouchableOpacity>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Notes</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={notes}
              onChangeText={setNotes}
              placeholder="Additional notes about the equipment"
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={4}
            />
          </View>

          {/* Additional Information Toggle */}
          <View style={styles.inputGroup}>
            <TouchableOpacity
              style={styles.toggleContainer}
              onPress={() => setShowAdditionalInfo(!showAdditionalInfo)}
            >
              <View style={[styles.toggle, showAdditionalInfo && styles.toggleActive]}>
                <View style={[styles.toggleThumb, showAdditionalInfo && styles.toggleThumbActive]} />
              </View>
              <Text style={styles.toggleLabel}>Add additional information about your medical equipment?</Text>
            </TouchableOpacity>
            <Text style={styles.helperText}>
              Include details about rental costs, contract dates, contact person, maintenance, and serial/model numbers.
            </Text>
          </View>

          {showAdditionalInfo && (
            <>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Rental Cost</Text>
                <TextInput
                  style={styles.input}
                  value={rentalCost}
                  onChangeText={setRentalCost}
                  placeholder="Monthly rental cost"
                  placeholderTextColor="#A9A9A9"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Start Date</Text>
                <TextInput
                  style={styles.input}
                  value={startDate}
                  onChangeText={setStartDate}
                  placeholder="Equipment start/rental date"
                  placeholderTextColor="#A9A9A9"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Contract End</Text>
                <TextInput
                  style={styles.input}
                  value={contractEnd}
                  onChangeText={setContractEnd}
                  placeholder="Contract end date"
                  placeholderTextColor="#A9A9A9"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Contact Person</Text>
                <TextInput
                  style={styles.input}
                  value={contactPerson}
                  onChangeText={setContactPerson}
                  placeholder="Primary contact person"
                  placeholderTextColor="#A9A9A9"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Maintenance Schedule</Text>
                <TextInput
                  style={[styles.input, styles.textArea]}
                  value={maintenanceSchedule}
                  onChangeText={setMaintenanceSchedule}
                  placeholder="Maintenance requirements and schedule"
                  placeholderTextColor="#A9A9A9"
                  multiline
                  numberOfLines={4}
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Serial Number</Text>
                <TextInput
                  style={styles.input}
                  value={serialNumber}
                  onChangeText={setSerialNumber}
                  placeholder="Equipment serial number"
                  placeholderTextColor="#A9A9A9"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Model Number</Text>
                <TextInput
                  style={styles.input}
                  value={modelNumber}
                  onChangeText={setModelNumber}
                  placeholder="Equipment model number"
                  placeholderTextColor="#A9A9A9"
                />
              </View>
            </>
          )}

          {showAddAnotherDone ? (
            <View style={styles.addAnotherDoneContainer}>
              <TouchableOpacity
                style={styles.addAnotherButton}
                onPress={handleAddAnother}
              >
                <Text style={styles.submitButtonText}>Add Another</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.doneButton}
                onPress={handleDone}
              >
                <Text style={styles.submitButtonText}>Done</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.submitButtonContainer}>
              {saving ? (
                <TouchableOpacity style={[styles.submitButton, styles.buttonDisabled]} disabled>
                  <ActivityIndicator color="#FFFFFF" />
                </TouchableOpacity>
              ) : (
                <TouchableOpacity
                  style={styles.submitButton}
                  onPress={handleSave}
                >
                  <Text style={styles.submitButtonText}>
                    {isEditing ? 'Update' : 'Submit'}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </ScrollView>
      </View>

      <Modal
        visible={showStateModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowStateModal(false)}
        statusBarTranslucent
        hardwareAccelerated={true} // ← ADDED THIS
      >
        <View style={styles.modalOverlay}>
          {/* This handles the transparent dimmed background and closes the modal */}
          <TouchableOpacity
            style={{ flex: 1 }}
            activeOpacity={1}
            onPress={() => setShowStateModal(false)}
          />

          {/* This is the actual modal content that should stay visible */}
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select State</Text>
              <TouchableOpacity
                style={styles.modalCloseButton}
                onPress={() => setShowStateModal(false)}
              >
                <Text style={styles.modalCloseText}>✕</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.stateList}>
              {US_STATES.map((state) => (
                <TouchableOpacity
                  key={state}
                  style={styles.stateItem}
                  onPress={() => handleStateSelect(state)}
                >
                  <Text style={styles.stateItemText}>{state}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
    paddingBottom: ms(120),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  textArea: {
    height: s(100),
    textAlignVertical: 'top',
  },
  submitButtonContainer: {
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(60),
    alignItems: 'center',
    marginBottom: ms(120),
    minWidth: s(200),
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  addAnotherDoneContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(120),
    gap: ms(20),
  },
  addAnotherButton: {
    backgroundColor: '#FF9F5A',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  doneButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  toggleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(10),
  },
  toggle: {
    width: s(50),
    height: s(30),
    borderRadius: ms(15),
    backgroundColor: '#E5E7EB',
    marginRight: ms(15),
    padding: ms(2),
    justifyContent: 'center',
  },
  toggleActive: {
    backgroundColor: '#29BFC0',
  },
  toggleThumb: {
    width: s(26),
    height: s(26),
    borderRadius: ms(13),
    backgroundColor: '#FFFFFF',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  toggleThumbActive: {
    alignSelf: 'flex-end',
  },
  toggleLabel: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    flex: 1,
  },
  helperText: {
    fontSize: ms(14, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    lineHeight: ms(18, 0.3),
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    maxHeight: '80%',
    width: '100%',
    paddingBottom: ms(20),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: ms(16),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  modalTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  modalCloseButton: {
    padding: ms(8),
  },
  modalCloseText: {
    fontSize: ms(20, 0.3),
    color: '#1C2C4F',
  },
  stateList: {
    padding: ms(16),
  },
  stateItem: {
    paddingVertical: ms(12),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  stateItemText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  inputText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  placeholderText: {
    fontSize: ms(16, 0.3),
    color: '#A9A9A9',
  },
});
